import type { ReactElement } from 'react';
import type { IconProps } from '../primitives/Icon';
import styles from './SegmentedNav.module.css';

type IconComponent = (props: IconProps) => ReactElement;

export interface Segment<T extends string> {
  id: T;
  label: string;
  Icon: IconComponent;
}

interface Props<T extends string> {
  segments: Segment<T>[];
  active: T;
  onChange: (id: T) => void;
  /** Touch-friendly mobile layout vs compact desktop. */
  size?: 'md' | 'sm';
  ariaLabel?: string;
}

/**
 * Pill-on-pill segmented nav shared by the mobile bottom tab bar and the
 * desktop header nav. The `size` prop controls visual density only — both
 * variants share the same interaction model, focus ring, and semantic
 * markup so screen readers see the same shape across viewports.
 */
export function SegmentedNav<T extends string>({
  segments,
  active,
  onChange,
  size = 'md',
  ariaLabel = 'Primary navigation',
}: Props<T>) {
  if (segments.length === 0) return null;
  return (
    <nav
      className={`${styles.group} ${size === 'sm' ? styles.sm : styles.md}`}
      aria-label={ariaLabel}
    >
      {segments.map(({ id, label, Icon }) => {
        const on = active === id;
        return (
          <button
            key={id}
            type="button"
            className={`${styles.segment} ${on ? styles.segmentOn : ''}`}
            onClick={() => onChange(id)}
            aria-current={on ? 'page' : undefined}
          >
            <Icon
              size={size === 'sm' ? 14 : 15}
              color="currentColor"
              strokeWidth={on ? 2 : 1.7}
            />
            <span className={styles.label}>{label}</span>
          </button>
        );
      })}
    </nav>
  );
}
